// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Ability from "./ability.js"
import Role from "./role.js"

const modelClassData = {
  "attributes": {
    "ability_id": {
      "column": {
        "default": null,
        "name": "ability_id",
        "null": false,
        "type": "uuid"
      },
      "name": "ability_id",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "role_id": {
      "column": {
        "default": null,
        "name": "role_id",
        "null": false,
        "type": "uuid"
      },
      "name": "role_id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "RoleAbility",
  "collectionKey": "role_abilities",
  "collectionName": "role_abilities",
  "i18nKey": "role_ability",
  "camelizedLower": "roleAbility",
  "name": "RoleAbility",
  "nameDasherized": "role-ability",
  "pluralName": "role_abilities",
  "ransackable_associations": [
    {
      "className": "Ability",
      "collectionName": "abilities",
      "foreignKey": "ability_id",
      "name": "ability",
      "macro": "belongs_to",
      "resource_name": "Ability",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Role",
      "collectionName": "roles",
      "foreignKey": "role_id",
      "name": "role",
      "macro": "belongs_to",
      "resource_name": "Role",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "ability_id",
      "column": {
        "default": null,
        "name": "ability_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "handled_by_system",
      "column": {
        "default": false,
        "name": "handled_by_system",
        "null": false,
        "type": "boolean"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "layouts",
      "column": {
        "default": null,
        "name": "layouts",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "old_ability_id",
      "column": {
        "default": null,
        "name": "old_ability_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_role_id",
      "column": {
        "default": null,
        "name": "old_role_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "role_id",
      "column": {
        "default": null,
        "name": "role_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Ability",
      "collectionName": "abilities",
      "foreignKey": "ability_id",
      "name": "ability",
      "macro": "belongs_to",
      "resource_name": "Ability",
      "through": null
    },
    {
      "className": "Role",
      "collectionName": "roles",
      "foreignKey": "role_id",
      "name": "role",
      "macro": "belongs_to",
      "resource_name": "Role",
      "through": null
    }
  ],
  "paramKey": "role_ability",
  "primaryKey": "id"
}

/** Frontend model for RoleAbility. */
export default class RoleAbility extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  abilityId() {
    return this.readAttributeUnderscore("ability_id")
  }

  /** @returns {boolean} */
  hasAbilityId() {
    const value = this.abilityId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  roleId() {
    return this.readAttributeUnderscore("role_id")
  }

  /** @returns {boolean} */
  hasRoleId() {
    const value = this.roleId()

    return this._isPresent(value)
  }

  /** @returns {import("./ability.js").default | null} */
  ability() {
    return this._readBelongsToReflection({modelClass: Ability, reflectionName: "ability"})
  }

  /** @returns {Promise<import("./ability.js").default | null>} */
  loadAbility() {
    if (!("abilityId" in this)) throw new Error("Foreign key method wasn't defined: abilityId")

    const id = this.abilityId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "ability",
        model: this,
        modelClass: Ability
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./ability.js").default | null>} */
  ensureAbilityLoaded() {
    return this.ensureAssociationLoaded("ability")
  }

  /** @returns {import("./role.js").default | null} */
  role() {
    return this._readBelongsToReflection({modelClass: Role, reflectionName: "role"})
  }

  /** @returns {Promise<import("./role.js").default | null>} */
  loadRole() {
    if (!("roleId" in this)) throw new Error("Foreign key method wasn't defined: roleId")

    const id = this.roleId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "role",
        model: this,
        modelClass: Role
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./role.js").default | null>} */
  ensureRoleLoaded() {
    return this.ensureAssociationLoaded("role")
  }
}
